import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import posthog from 'posthog-js';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import HelpGuide from './HelpGuide';
import ChangelogList, { formatDate } from './ChangelogList';
import { SURVEY_URL } from '../lib/survey';

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_DOT = { ok: null, degraded: 'bg-amber-500', outage: 'bg-red-500' };
const STATUS_CONFIG = {
    ok: { dot: 'bg-green-500', label: 'All systems operational', bg: 'bg-green-50 border-green-200 text-green-800' },
    degraded: { dot: 'bg-amber-500', label: 'Degraded performance', bg: 'bg-amber-50 border-amber-200 text-amber-800' },
    outage: { dot: 'bg-red-500', label: 'Service disruption', bg: 'bg-red-50 border-red-200 text-red-800' },
};

// ── Changelog modal content ────────────────────────────────────────────────
function ChangelogPanel({ onClose }) {
    const [status, setStatus] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        Promise.all([
            fetch(`${base}/api/status`).then(r => r.json()).catch(() => null),
            // Logged-out visitors can open this dialog, and the changelog is
            // account holders only, so it is simply left empty for them.
            (localStorage.getItem('token')
                ? fetch(`${base}/api/changelog?limit=100`, { headers: authHeaders() }).then(r => r.json())
                : Promise.resolve([])).catch(() => []),
        ]).then(([s, e]) => {
            setStatus(s);
            setEntries(Array.isArray(e) ? e : []);
        }).finally(() => setLoading(false));
    }, []);

    const cfg = STATUS_CONFIG[status?.level] || STATUS_CONFIG.ok;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="bg-white max-w-md max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Mimique Status</DialogTitle>
                </DialogHeader>
                <div className="overflow-y-auto flex-1 space-y-5 pr-1">
                    {/* Status bar */}
                    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${cfg.bg}`}>
                        <span className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cfg.dot}`} />
                        <div>
                            <p className="font-semibold text-sm">{status?.message || cfg.label}</p>
                            {status?.updated_at && (
                                <p className="text-xs opacity-70 mt-0.5">Updated {formatDate(status.updated_at)}</p>
                            )}
                        </div>
                    </div>

                    <p className="text-sm font-semibold text-foreground">Changelog</p>

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : entries.length === 0 ? (
                        <p className="text-stone-400 text-sm text-center py-6">No updates yet.</p>
                    ) : (
                        <ChangelogList entries={entries} variant="modal" />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ── Help modal content ─────────────────────────────────────────────────────
// The guide itself lives in the shared <HelpGuide /> component so the modal and
// the /help page stay in sync (single source of truth).
function HelpPanel({ onClose }) {
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="bg-white max-w-md max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-center">Mimique Help Guide 💚</DialogTitle>
                </DialogHeader>
                <p className="text-center text-xs text-stone-400 -mt-1 mb-1">Tap any card to learn more and watch video guides</p>
                <div className="overflow-y-auto flex-1 pr-1">
                    <HelpGuide compact />
                </div>
            </DialogContent>
        </Dialog>
    );
}



// ── Main component ─────────────────────────────────────────────────────────
export default function FeedbackButton() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [panel, setPanel] = useState(null); // 'changelog' | 'help' | 'survey'
    const [message, setMessage] = useState('');
    const [appStatus, setAppStatus] = useState(null);
    // Hidden on first paint of every page so it can't sit on top of the
    // welcome/header text at scroll-top; appears the moment the user scrolls.
    // Fallback: if the page doesn't need to scroll (content shorter than the
    // viewport), reveal after a short delay — otherwise the button would be
    // trapped hidden forever on short screens.
    const [showButton, setShowButton] = useState(false);

    // Pulse the help icon after the user taps the "Find help videos" checklist
    // item on the Dashboard (which sets 'help_wanted' in their clicked set).
    // Stops the moment they click here (which marks 'help_visited' done).
    // Whole-checklist dismissal also kills the pulse either way.
    const readShouldPulse = () => {
        if (!user?.id) return false;
        try {
            if (localStorage.getItem(`onboarding_checklist_dismissed_${user.id}`) === '1') return false;
            const clicked = JSON.parse(localStorage.getItem(`onboarding_checklist_clicked_${user.id}`) || '[]');
            const done = JSON.parse(localStorage.getItem(`onboarding_checklist_done_${user.id}`) || '[]');
            return clicked.includes('help_wanted') && !done.includes('help_visited');
        } catch { return false; }
    };
    const [shouldPulseHelp, setShouldPulseHelp] = useState(false);
    useEffect(() => {
        setShouldPulseHelp(readShouldPulse());
        const refresh = () => setShouldPulseHelp(readShouldPulse());
        window.addEventListener('onboarding-checklist-updated', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('onboarding-checklist-updated', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setShowButton(false);
        const reveal = () => setShowButton(true);
        const onScroll = () => { if (window.scrollY > 10) reveal(); };
        window.addEventListener('scroll', onScroll, { passive: true });
        // If already scrolled (e.g. anchor link, back-nav restoring position), show now.
        if (window.scrollY > 10) reveal();
        // Fallback for pages that can't scroll: reveal after a short delay so
        // the initial headings stay unobstructed but the button is still reachable.
        let fallback = null;
        const armFallback = () => {
            if (fallback) return;
            const canScroll = document.documentElement.scrollHeight > window.innerHeight + 20;
            if (!canScroll) fallback = setTimeout(reveal, 1500);
        };
        // Wait a tick so layout has settled before measuring.
        const armTimer = setTimeout(armFallback, 200);
        return () => {
            window.removeEventListener('scroll', onScroll);
            clearTimeout(armTimer);
            if (fallback) clearTimeout(fallback);
        };
    }, [location.pathname]);

    useEffect(() => {
        if (!open) return;
        const base = process.env.REACT_APP_BACKEND_URL || '';
        fetch(`${base}/api/status`).then(r => r.json()).then(setAppStatus).catch(() => null);
    }, [open]);

    if (!user) return null;

    const openPanel = (name) => { setOpen(false); setPanel(name); };
    const closePanel = () => setPanel(null);

    // PostHog external surveys are anonymous unless the person is named in the
    // URL — the survey opens on posthog.com, so our in-app identity does NOT
    // follow it. distinct_id MUST match what we pass to posthog.identify()
    // (user.id, see AuthContext), otherwise the response lands on a separate
    // anonymous person with no email. Always open the survey through this.
    const openSurvey = () => {
        try { posthog.identify(user.id, { email: user.email, name: user.name }); } catch { /* noop */ }
        window.open(`${SURVEY_URL}?distinct_id=${encodeURIComponent(user.id)}`, '_blank', 'noopener,noreferrer');
    };

    const handleSubmit = () => {
        if (!message.trim()) return;
        posthog.identify(user?.id, { email: user?.email, name: user?.name });
        posthog.capture('user_feedback', { message: message.trim(), page: location.pathname, email: user?.email, name: user?.name });
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: message.trim(), email: user?.email, user_id: user?.id, page: location.pathname }),
        }).then(r => r.json()).then(j => { if (!j.ok) console.error('Feedback email failed:', j.error); })
          .catch(err => console.error('Feedback email failed:', err));
        toast.success('Thank you for your message, I will look into the issue ASAP!');
        setMessage('');
        setOpen(false);
    };

    // Force the icon visible while it should pulse — otherwise the user is
    // told to "look top right" but the icon might still be hidden waiting on
    // a scroll gesture that never comes.
    const iconVisible = showButton || shouldPulseHelp;
    const handleOpen = () => {
        setOpen(true);
        // Opening the panel = the user has found where help lives. Mark done.
        if (user?.id) {
            try {
                const key = `onboarding_checklist_done_${user.id}`;
                const arr = JSON.parse(localStorage.getItem(key) || '[]');
                if (!arr.includes('help_visited')) {
                    arr.push('help_visited');
                    localStorage.setItem(key, JSON.stringify(arr));
                    window.dispatchEvent(new CustomEvent('onboarding-checklist-updated'));
                }
            } catch { /* localStorage disabled — safe to ignore */ }
        }
    };
    return (
        <>
            <button
                onClick={handleOpen}
                className={`fixed top-4 right-4 z-50 flex items-center justify-center w-8 h-8 rounded-full text-accent hover:text-white transition-all duration-200 ${iconVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${shouldPulseHelp ? 'animate-onboarding-glow' : ''}`}
                aria-label="Help and feedback"
                aria-hidden={!iconVisible}
                tabIndex={iconVisible ? 0 : -1}
            >
                <HelpCircle className="h-6 w-6" />
            </button>

            {/* Main modal. max-h + inner scroll so tall content (down to the survey
                button) stays reachable and the close button stays on-screen on short
                viewports — same pattern as the Changelog/Help panels. */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white max-w-sm max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Help &amp; Feedback</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 overflow-y-auto flex-1 pr-1">

                        {/* Section 1 — Help & Changelog. Always stacked: the dialog is
                            narrow at every screen size, so viewport breakpoints don't apply. */}
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => openPanel('help')}
                                className="flex-1 rounded-xl border border-stone-200 px-4 py-3 text-center hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                            >
                                <p className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">Help guide</p>
                                <p className="text-xs text-stone-400 mt-0.5">Discover all the features and how to use</p>
                            </button>
                            <button
                                onClick={() => openPanel('changelog')}
                                className="flex-1 rounded-xl border border-stone-200 px-4 py-3 text-center hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                            >
                                <p className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">What's new</p>
                                <p className="text-xs text-stone-400 mt-0.5">Changelog & status</p>
                            </button>
                        </div>

                        {/* Section 2 — Feedback survey. Hidden for launch; the
                            wiring (openSurvey, SURVEY_URL) is left intact so it
                            can be brought back by flipping this to true. */}
                        {false && (
                            <>
                                <div className="flex items-center gap-3">
                                    <hr className="flex-1 border-stone-200" />
                                    <span className="text-xs font-medium uppercase tracking-widest text-stone-400">or</span>
                                    <hr className="flex-1 border-stone-200" />
                                </div>

                                <div className="rounded-xl border border-primary/20 bg-secondary p-4 text-center space-y-3">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-primary">Feedback Survey 2</p>
                                    <p className="text-sm text-stone-600 leading-snug">
                                        This is my second survey, to help get Mimique ready for launch. Five minutes of your honest thoughts would really help 💚
                                    </p>
                                    <button
                                        onClick={() => { openSurvey(); setOpen(false); }}
                                        className="block w-full text-center py-2.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-colors shadow-sm"
                                    >
                                        Give your feedback
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                            <hr className="flex-1 border-stone-200" />
                            <span className="text-xs font-medium uppercase tracking-widest text-stone-400">or</span>
                            <hr className="flex-1 border-stone-200" />
                        </div>

                        {/* Section 3 — Report an issue */}
                        <div className="space-y-2 text-center">
                            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400">Report an issue</p>
                            <textarea
                                className="w-full p-3 border border-stone-200 rounded-lg text-sm resize-none bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                                rows={3}
                                placeholder="Describe the problem, including any error message."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                            <div className="flex justify-center">
                                <Button
                                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                                    onClick={handleSubmit}
                                    disabled={!message.trim()}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Sub-panels */}
            {panel === 'changelog' && <ChangelogPanel onClose={closePanel} />}
            {panel === 'help'      && <HelpPanel      onClose={closePanel} />}
        </>
    );
}
